import { ZONE_COLORS } from "../AttributeSumLineChart/utils/thresholdUtils";

export { ZONE_COLORS };

// ── X-axis label formatter (matches OverdueValueTimeline / AttributeSumLineChart style) ──
const MONTH_NAMES_DE = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

export function formatXLabel(isoDate, granularity) {
  if (!isoDate) return "";
  const d = new Date(isoDate + "T00:00:00Z");
  const day = d.getUTCDate();
  const month = MONTH_NAMES_DE[d.getUTCMonth()];
  const year = d.getUTCFullYear();

  if (granularity === "year") return String(year);
  if (granularity === "month") return `${month} ${year}`;
  if (granularity === "week") {
    const tmp = new Date(Date.UTC(year, d.getUTCMonth(), d.getUTCDate()));
    const dayOfWeek = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayOfWeek);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
    return `KW ${weekNo}`;
  }
  return `${day}. ${month}`;
}

function bucketKeyForGranularity(isoDate, granularity) {
  if (granularity === "day") return isoDate;
  const d = new Date(isoDate + "T00:00:00Z");
  if (granularity === "week") {
    const dow = d.getUTCDay() || 7;
    const mon = new Date(d);
    mon.setUTCDate(d.getUTCDate() - dow + 1);
    return mon.toISOString().slice(0, 10);
  }
  if (granularity === "month") {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
  }
  return `${d.getUTCFullYear()}-01-01`; // year
}

/**
 * Buckets the (already non-cumulative, per-day) eligible/on_time/late counts from
 * /kpi/liefertermintreue-timeline into day/week/month/year periods, summing counts
 * within each bucket, and derives the compliance rate from the summed counts
 * (never by averaging daily rates — that would weight days unequally).
 */
export function bucketCountsByGranularity(dataPoints, granularity) {
  if (!dataPoints?.length) return [];
  if (granularity === "day") {
    return dataPoints.map((p) => ({
      date: p.date,
      eligible_count: p.eligible_count,
      on_time_count: p.on_time_count,
      late_count: p.late_count,
      rate: p.eligible_count > 0 ? (p.on_time_count / p.eligible_count) * 100 : null,
    }));
  }
  const buckets = new Map();
  for (const p of dataPoints) {
    const key = bucketKeyForGranularity(p.date, granularity);
    if (!buckets.has(key)) {
      buckets.set(key, { date: key, eligible_count: 0, on_time_count: 0, late_count: 0 });
    }
    const b = buckets.get(key);
    b.eligible_count += p.eligible_count;
    b.on_time_count += p.on_time_count;
    b.late_count += p.late_count;
  }
  return Array.from(buckets.values())
    .map((b) => ({ ...b, rate: b.eligible_count > 0 ? (b.on_time_count / b.eligible_count) * 100 : null }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Trailing rolling-window smoothing over the raw daily counts: for each day D,
 * sums eligible_count/on_time_count over the last `windowDays` days ending at D
 * (inclusive), then derives the rate from the summed counts. Always computed at
 * daily resolution regardless of the display granularity — callers sample the
 * resulting series at whichever days they display.
 */
export function computeRollingSeries(dataPoints, windowDays) {
  if (!dataPoints?.length) return [];
  const result = [];
  let eligibleSum = 0;
  let onTimeSum = 0;
  const window = [];
  for (const p of dataPoints) {
    window.push(p);
    eligibleSum += p.eligible_count;
    onTimeSum += p.on_time_count;
    if (window.length > windowDays) {
      const dropped = window.shift();
      eligibleSum -= dropped.eligible_count;
      onTimeSum -= dropped.on_time_count;
    }
    result.push({
      date: p.date,
      eligible_count: eligibleSum,
      on_time_count: onTimeSum,
      rate: eligibleSum > 0 ? (onTimeSum / eligibleSum) * 100 : null,
    });
  }
  return result;
}

/**
 * Maps a rolling daily series onto the bucket dates produced by
 * bucketCountsByGranularity (day/week/month/year), sampling the rolling value
 * at the LAST day of each bucket — the trailing window already carries the
 * smoothing, so bucket points only need to be "read off" at each bucket's end.
 */
export function sampleRollingAtBuckets(rollingDaily, bucketedPoints, granularity) {
  if (!bucketedPoints?.length) return [];
  if (granularity === "day") return rollingDaily;

  const byDate = new Map(rollingDaily.map((p) => [p.date, p]));
  const sortedDates = rollingDaily.map((p) => p.date).sort();

  return bucketedPoints.map((bucket, idx) => {
    const nextBucketDate = bucketedPoints[idx + 1]?.date ?? null;
    // last raw day strictly before the next bucket's start (or the last available day)
    let lastDay = null;
    for (const d of sortedDates) {
      if (nextBucketDate && d >= nextBucketDate) break;
      lastDay = d;
    }
    return byDate.get(lastDay) ?? { date: bucket.date, eligible_count: 0, on_time_count: 0, rate: null };
  });
}

/**
 * Merges two DelayGroupStats-shaped objects ({count, sum, min, max}) into one
 * combined stat — sum/count division is exact (the backend supplies `sum`
 * directly, no avg*count reconstruction trick needed). `emptyValue` controls
 * what average/min/max resolve to when the combined count is 0: pass `null`
 * for the "all" population (a real gap — genuinely nothing due yet) or `0`
 * for "late_only" (a real "nobody late" reading — keeps that line connected).
 */
function combineGroups(a, b, emptyValue) {
  const count = (a?.count ?? 0) + (b?.count ?? 0);
  const sum = (a?.sum ?? 0) + (b?.sum ?? 0);
  if (count === 0) return { count: 0, sum: 0, average: emptyValue, min: emptyValue, max: emptyValue };
  const mins = [a?.min, b?.min].filter((v) => v != null);
  const maxs = [a?.max, b?.max].filter((v) => v != null);
  return {
    count,
    sum,
    average: sum / count,
    min: mins.length ? Math.min(...mins) : emptyValue,
    max: maxs.length ? Math.max(...maxs) : emptyValue,
  };
}

/**
 * Derives, for each raw daily DataPoint from /kpi/absolute-delay-timeline, the
 * single-day ("Roh") combined stat for the given population — merging that
 * day's `open` group with `completed_all` or `completed_late_only` (per
 * `population`), plus the backend-precomputed exact single-day median. Must
 * run BEFORE sampleSnapshotByGranularity. Also carries the raw
 * `openGroup`/`completedGroup` through for the tooltip breakdown, and derives
 * the legacy-shaped evaluated/late/on-time counts (the old top-level count
 * fields no longer exist on the payload).
 */
export function deriveDailyDelayStats(dataPoints, population) {
  if (!dataPoints?.length) return [];
  const completedKey = population === "all" ? "completed_all" : "completed_late_only";
  const medianKey = population === "all" ? "combined_all_median" : "combined_late_only_median";
  const emptyValue = population === "all" ? null : 0;

  return dataPoints.map((p) => {
    const open = p.open ?? { count: 0, sum: 0, min: null, max: null };
    const completed = p[completedKey] ?? { count: 0, sum: 0, min: null, max: null };
    const combined = combineGroups(open, completed, emptyValue);
    return {
      date: p.date,
      average: combined.average,
      min: combined.min,
      max: combined.max,
      sum: combined.sum,
      median: p[medianKey] ?? emptyValue,
      openGroup: open,
      completedGroup: completed,
      evaluatedCount: open.count + (p.completed_all?.count ?? 0),
      lateCount: open.count + (p.completed_late_only?.count ?? 0),
      onTimeCount: (p.completed_all?.count ?? 0) - (p.completed_late_only?.count ?? 0),
    };
  });
}

/**
 * Trailing rolling-window smoothing for the absolute-delay timeline. `open`
 * is a WIP snapshot and is NEVER summed across multiple days — only the
 * `open` group of the currently displayed day D participates.
 * `completed_all`/`completed_late_only` ARE additive cohorts, so their
 * count/sum/min/max are summed/nested across the trailing window
 * [D-windowDays+1, D]. average/min/max/sum are then exact. Median is
 * deliberately NOT produced here — see combineGroups/backend docstrings for
 * why it can't be reconstructed across a window. Callers must not show a
 * smoothed line when aggregation === "median".
 */
export function computeRollingDelayStats(dataPoints, windowDays, population) {
  if (!dataPoints?.length) return [];
  const completedKey = population === "all" ? "completed_all" : "completed_late_only";
  const emptyValue = population === "all" ? null : 0;

  const window = [];
  let sumCount = 0;
  let sumSum = 0;
  const result = [];

  for (const p of dataPoints) {
    const g = p[completedKey] ?? { count: 0, sum: 0, min: null, max: null };
    window.push(g);
    sumCount += g.count;
    sumSum += g.sum;
    if (window.length > windowDays) {
      const dropped = window.shift();
      sumCount -= dropped.count;
      sumSum -= dropped.sum;
    }

    const mins = window.map((w) => w.min).filter((v) => v != null);
    const maxs = window.map((w) => w.max).filter((v) => v != null);
    const windowedCompleted = {
      count: sumCount,
      sum: sumSum,
      min: mins.length ? Math.min(...mins) : null,
      max: maxs.length ? Math.max(...maxs) : null,
    };

    const open = p.open ?? { count: 0, sum: 0, min: null, max: null };
    const combined = combineGroups(open, windowedCompleted, emptyValue);
    result.push({ date: p.date, average: combined.average, min: combined.min, max: combined.max, sum: combined.sum });
  }
  return result;
}

/**
 * For the absolute-delay timeline: since min/median/average/max are not
 * additive across days, granularity buckets are resampled by taking the value
 * from the LAST day of each bucket period (a point-in-time snapshot) instead
 * of pooling/averaging multiple days together.
 */
export function sampleSnapshotByGranularity(dataPoints, granularity) {
  if (!dataPoints?.length) return [];
  if (granularity === "day") return dataPoints;

  const buckets = new Map();
  for (const p of dataPoints) {
    const key = bucketKeyForGranularity(p.date, granularity);
    buckets.set(key, p); // later (chronologically later) days overwrite earlier ones -> last day wins
  }
  return Array.from(buckets.entries())
    .map(([key, p]) => ({ ...p, date: key }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// ── Threshold classification (JS port of kpi/service.py's
//    _classify_target_based / _classify_optimal_range) ──────────────────────

export function classifyTargetBased(value, target, warningBoundary, isMore) {
  if (value == null || target == null || warningBoundary == null) return null;
  if (isMore) {
    if (value >= target) return "good";
    if (value >= warningBoundary) return "warning";
    return "bad";
  }
  if (value <= target) return "good";
  if (value <= warningBoundary) return "warning";
  return "bad";
}

export function classifyOptimalRange(value, lower, upper) {
  if (value == null || lower == null || upper == null) return null;
  if (value >= lower && value <= upper) return "good";
  return "bad";
}

/**
 * config: { thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper }
 */
export function classifyThresholdValue(value, config) {
  if (!config?.thresholdType || value == null) return null;
  const { thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper } = config;
  if (thresholdType === "optimal_range") {
    return classifyOptimalRange(value, thresholdLower, thresholdUpper);
  }
  if (thresholdTarget == null || thresholdWarningPercentage == null) return null;
  const warningBoundary = thresholdTarget * (thresholdWarningPercentage / 100);
  return classifyTargetBased(value, thresholdTarget, warningBoundary, thresholdType === "more_the_better");
}
